import { useState } from "react";
import Button from "../ui/Button.jsx";
import {
  validateEntraConnectFields,
  mapEntraConnectApiError,
} from "./entraConnectValidation.js";
import { ENTRA_M2M_GUIDANCE } from "./entraConnectGuidance.js";
import ProviderFrameworkPicker, { normalizeFrameworksForProviderSubmit } from "./ProviderFrameworkPicker.jsx";
import { getDefaultProviderComplianceFrameworks } from "../../utils/providerFrameworks.js";

/**
 * @param {{ onBack: () => void, onConnected: (projectId: string) => void }} props
 */
export default function ConnectEntraPanel({ onBack, onConnected }) {
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [frameworks, setFrameworks] = useState(() => getDefaultProviderComplianceFrameworks("entra"));
  const [showSecret, setShowSecret] = useState(false);
  const [testOk, setTestOk] = useState(false);
  const [tenantDisplayName, setTenantDisplayName] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetTest = () => {
    setTestOk(false);
    setTenantDisplayName("");
  };

  const handleTest = async () => {
    setError(null);
    resetTest();
    const validated = validateEntraConnectFields({
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setTestBusy(true);
    try {
      const res = await fetch("/api/idp/entra/test-connection", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: validated.tenant_id,
          client_id: validated.client_id,
          client_secret: validated.client_secret,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setError(mapEntraConnectApiError(data));
        return;
      }
      setTestOk(true);
      setTenantDisplayName(String(data.tenant_display_name || validated.tenant_id));
    } catch (e) {
      setError(e?.message || "Test connection failed");
    } finally {
      setTestBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!testOk) return;
    setError(null);
    const validated = validateEntraConnectFields({
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
    });
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/idp/entra/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: validated.tenant_id,
          client_id: validated.client_id,
          client_secret: validated.client_secret,
          display_name: displayName.trim() || undefined,
          environment:
            environment === "production"
              ? "Production"
              : environment === "sandbox"
                ? "Preview / Sandbox"
                : "Development",
          complianceFrameworks: normalizeFrameworksForProviderSubmit("entra", frameworks),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.project?.projectId) {
        throw new Error(mapEntraConnectApiError(data));
      }
      const projectId = data.project.projectId;
      try {
        sessionStorage.setItem("idwiz.activeProjectId", projectId);
      } catch {
        /* ignore */
      }
      onConnected?.(projectId);
    } catch (err) {
      setError(err?.message || "Connection failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 32px 8px" }}>
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "16px", whiteSpace: "pre-line" }}>
          {ENTRA_M2M_GUIDANCE}
        </p>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Directory (tenant) ID
          </span>
          <input
            required
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              resetTest();
            }}
            placeholder="00000000-0000-0000-0000-000000000000"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Application (client) ID
          </span>
          <input
            required
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              resetTest();
            }}
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Client secret
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              required
              type={showSecret ? "text" : "password"}
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                resetTest();
              }}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="button" onClick={() => setShowSecret((s) => !s)} style={toggleBtnStyle}>
              {showSecret ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <label style={{ display: "block", marginBottom: "14px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Display name (optional)
          </span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Contoso — Entra production"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "20px" }}>
          <span className="text-secondary" style={{ fontSize: "12px", display: "block", marginBottom: "6px" }}>
            Environment
          </span>
          <select value={environment} onChange={(e) => setEnvironment(e.target.value)} style={inputStyle}>
            <option value="production">Production</option>
            <option value="sandbox">Preview / Sandbox</option>
            <option value="development">Development</option>
          </select>
        </label>

        <div style={{ marginBottom: "16px" }}>
          <div className="text-secondary" style={{ fontSize: "12px", marginBottom: "8px" }}>
            Compliance frameworks
          </div>
          <ProviderFrameworkPicker providerId="entra" value={frameworks} onChange={setFrameworks} />
        </div>

        {testOk ? (
          <p style={{ fontSize: "13px", color: "var(--status-ok, #4ade80)", marginBottom: "8px" }}>
            ✓ Connection verified — {tenantDisplayName || "tenant reachable"}
          </p>
        ) : null}

        {error ? (
          <p className="text-critical" style={{ fontSize: "13px" }}>
            {error}
          </p>
        ) : null}
      </div>

      <div style={footerStyle}>
        {!testOk && !testBusy ? (
          <span className="text-secondary" style={{ fontSize: "11px", marginRight: "auto" }}>
            Test connection before creating the project.
          </span>
        ) : null}
        <Button type="button" variant="ghost" onClick={onBack} disabled={loading || testBusy}>
          Back
        </Button>
        <Button type="button" variant="secondary" disabled={testBusy || loading} onClick={() => void handleTest()}>
          {testBusy ? "Testing…" : "Test Connection"}
        </Button>
        <Button type="submit" variant="primary" loading={loading} disabled={loading || !testOk}>
          {loading ? "Creating…" : "Create Project"}
        </Button>
      </div>
    </form>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "4px",
  border: "1px solid var(--border)",
  background: "var(--bg-input)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const toggleBtnStyle = {
  background: "var(--bg-elevated)",
  border: "1px solid var(--border)",
  borderRadius: "4px",
  padding: "0 12px",
  cursor: "pointer",
  color: "var(--text-secondary)",
};

const footerStyle = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 32px 24px",
  borderTop: "1px solid var(--border)",
};
